import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  createWidgetSkeletonDefinition,
  getTypographyLineStyle,
  HtmlSlot,
} from '../shared/widget-skeleton'

const ITEMS_SLOT = '{{items}}'
const SLOT_CLASS_NAME = 'haus-order-confirmation-skeleton__slot'
const ORDER_LABEL_VARIANT_SLOT = '{{orderLabelVariant}}'
const ORDER_VALUE_VARIANT_SLOT = '{{orderValueVariant}}'

const SKELETON_PRODUCT_LINES = [1, 2]
const SKELETON_HEADER_BLOCKS = [1, 2, 3, 4]

function OrderConfirmationSkeletonItem({
  orderLabelVariant,
  orderValueVariant,
}: {
  orderLabelVariant: string
  orderValueVariant: string
}) {
  return (
    <article className="haus-order-confirmation-skeleton__item" aria-hidden="true">
      <div className="haus-order-confirmation-skeleton__header">
        <div className="haus-order-confirmation-skeleton__headerInner">
          <div className="haus-order-confirmation-skeleton__headerInfo">
            {SKELETON_HEADER_BLOCKS.map((block) => (
              <div key={block} className="haus-order-confirmation-skeleton__headerBlock">
                <span
                  className="haus-order-confirmation-skeleton__line haus-order-confirmation-skeleton__lineLabel"
                  style={getTypographyLineStyle(orderLabelVariant, '45%')}
                />
                <span
                  className="haus-order-confirmation-skeleton__line haus-order-confirmation-skeleton__lineValue"
                  style={getTypographyLineStyle(orderValueVariant, '55%')}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="haus-order-confirmation-skeleton__productSection">
        <ul className="haus-order-confirmation-skeleton__productLineList">
          {SKELETON_PRODUCT_LINES.map((line) => (
            <li key={line} className="haus-order-confirmation-skeleton__productLine">
              <div className="haus-order-confirmation-skeleton__productLineLeft">
                <span className="haus-order-confirmation-skeleton__productImage" />
                <div className="haus-order-confirmation-skeleton__productLineMain">
                  <span className="haus-order-confirmation-skeleton__line haus-order-confirmation-skeleton__productName" />
                  <span className="haus-order-confirmation-skeleton__line haus-order-confirmation-skeleton__productQuantity" />
                </div>
              </div>
              <span className="haus-order-confirmation-skeleton__line haus-order-confirmation-skeleton__productPrice" />
            </li>
          ))}
        </ul>
      </div>
    </article>
  )
}

export function OrderConfirmationSkeleton(): ReactElement {
  const { t } = useTranslation()
  return (
    <section
      className="haus-order-confirmation-skeleton"
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label={t('a11y.orderConfirmation.aria.loading')}
    >
      <div className="haus-order-confirmation-skeleton__list">
        <OrderConfirmationSkeletonItem orderLabelVariant="body" orderValueVariant="body" />
      </div>
    </section>
  )
}

function OrderConfirmationSkeletonTemplate(): ReactElement {
  return (
    <section
      className="haus-order-confirmation-skeleton"
      data-widget-skeleton="order-confirmation"
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading order confirmation"
    >
      <div className="haus-order-confirmation-skeleton__list">
        <HtmlSlot html={ITEMS_SLOT} className={SLOT_CLASS_NAME} />
      </div>
    </section>
  )
}

export const widgetSkeleton = createWidgetSkeletonDefinition({
  widgetType: 'order-confirmation',
  fragments: {
    item: (
      <OrderConfirmationSkeletonItem
        orderLabelVariant={ORDER_LABEL_VARIANT_SLOT}
        orderValueVariant={ORDER_VALUE_VARIANT_SLOT}
      />
    ),
  },
  slots: {
    items: ITEMS_SLOT,
  },
  styleFile: './OrderConfirmationSkeleton.css',
  template: <OrderConfirmationSkeletonTemplate />,
})
